import { CROPS, CROP_ORDER } from "./catalog";
import { clamp } from "./math";
import { confine } from "./rules";
import { World } from "./world";
import { RESOURCES } from "./resources";
import { formation } from "./steering";
import { readEquipment } from "./equipment";
import type { CompanionCommand, CropId, Vec2 } from "./types";

export const SAVE_KEY = "verdant-isles-save-v3";
export const PREVIOUS_KEY = "verdant-isles-save-v2";
export const LEGACY_KEY = "verdant-isles-save-v1";
type RecordData = Record<string, unknown>;
const record = (value: unknown): RecordData =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as RecordData)
    : {};
const number = (
  value: unknown,
  fallback: number,
  min = 0,
  max = 1e5,
): number =>
  typeof value === "number" && Number.isFinite(value)
    ? clamp(value, min, max)
    : fallback;
const integer = (value: unknown, fallback: number, max = 1e5): number =>
  Math.floor(number(value, fallback, 0, max));
const cropId = (value: unknown): CropId | null =>
  typeof value === "string" && CROP_ORDER.includes(value as CropId)
    ? (value as CropId)
    : null;
const point = (value: unknown, fallback: Vec2): Vec2 => {
  const data = record(value);
  if (
    typeof data.x !== "number" ||
    typeof data.y !== "number" ||
    !Number.isFinite(data.x) ||
    !Number.isFinite(data.y) ||
    data.x < -630 ||
    data.x > 2230 ||
    data.y < 160 ||
    data.y > 810
  )
    return { ...fallback };
  const pos = {
    x: number(data.x, fallback.x, -630, 2230),
    y: number(data.y, fallback.y, 160, 810),
  };
  confine(pos);
  return pos;
};

export function serialize(world: World): string {
  return JSON.stringify({
    version: 3,
    extensionVersion: 1,
    equipment: world.equipment,
    inventory: world.inventory,
    seeds: world.seeds,
    plots: world.persistentPlots.map((p) => ({
      ...p,
      animation: undefined,
      stride: undefined,
      motion: undefined,
      statuses: undefined,
    })),
    companions: world.companions.map((c, slot) => ({
      ...c,
      // 远征坐标属于另一场景，存档回庭园时不能把队伍留在矿坑同名坐标。
      pos: world.homePosition ? formation(world.homePosition, slot) : c.pos,
      anchor: world.homePosition
        ? formation(world.homePosition, slot)
        : c.anchor,
      command: world.homePosition ? "follow" : c.command,
      animation: undefined,
      stride: undefined,
      workTargetId: undefined,
      motion: undefined,
      statuses: undefined,
      behavior: undefined,
      gaitMoving: undefined,
      home: undefined,
    })),
    spoilingFood: world.spoilingFood,
    resourceNodes: world.persistentResources.map((n) => ({
      id: n.id,
      health: n.health,
      active: n.active,
      respawnTimer: n.respawnTimer,
    })),
    progress: world.progress,
    player: {
      ...world.player,
      pos: world.homePosition ?? world.player.pos,
      animation: undefined,
      stride: undefined,
      motion: undefined,
      statuses: undefined,
      gaitMoving: undefined,
      behavior: undefined,
    },
    attackMode: world.attackMode,
    day: world.day,
    dayTime: world.dayTime,
    elapsed: world.elapsed,
  });
}

/** v1 仅迁移可靠的长期字段；损坏字段逐项回退，不能让一条坏记录毁掉整片庭园。 */
export function deserialize(raw: string): World | null {
  let data: RecordData;
  try {
    data = record(JSON.parse(raw));
  } catch {
    return null;
  }
  if (data.version !== 1 && data.version !== 2 && data.version !== 3)
    return null;
  const world = new World(),
    bag = record(data.inventory),
    seeds = record(data.seeds);
  for (const key of Object.keys(world.inventory) as Array<
    keyof typeof world.inventory
  >)
    world.inventory[key] = integer(bag[key], world.inventory[key]);
  for (const id of CROP_ORDER)
    world.seeds[id] = integer(seeds[id], world.seeds[id]);
  const progress = record(data.progress);
  for (const key of [
    "watered",
    "awakened",
    "gathered",
    "crafted",
    "victories",
    "quest",
    "coins",
    "tools",
    "soil",
    "capacity",
    "order",
    "orderBaseline",
  ] as const) {
    world.progress[key] = integer(
      progress[key],
      world.progress[key],
      key === "quest"
        ? 6
        : key === "tools"
          ? 3
          : key === "soil" || key === "capacity"
            ? 2
            : 1e5,
    );
  }
  for (const id of CROP_ORDER)
    world.progress.harvests[id] = integer(record(progress.harvests)[id], 0);
  // 五种作物的旧委托保留当前目标，不能因为扩充列表就悄悄换订单。
  if (data.version !== 3)
    world.progress.order =
      Math.floor(world.progress.order / 5) * 7 + (world.progress.order % 5);
  if (Array.isArray(data.plots))
    for (const plot of world.plots) {
      const saved = record(data.plots.find((v) => record(v).id === plot.id));
      if (Object.keys(saved).length === 0) continue;
      plot.cropId = cropId(saved.cropId);
      plot.growth = plot.cropId ? number(saved.growth, 0, 0, 1) : 0;
      plot.stage = !plot.cropId
        ? "empty"
        : plot.growth >= 1
          ? "mature"
          : plot.growth >= 0.72
            ? "flower"
            : plot.growth >= 0.45
              ? "leaf"
              : plot.growth >= 0.2
                ? "sprout"
                : "seed";
      plot.moisture = number(saved.moisture, 0.58, 0, 1);
      plot.fertility = number(saved.fertility, 0.58, 0, 1);
      plot.quality = number(saved.quality, 0.5, 0, 1);
      plot.careSamples = number(saved.careSamples, 0);
      plot.care = number(saved.care, 0, 0, plot.careSamples);
      plot.sprinkler = saved.sprinkler === true;
      plot.lastCrop = cropId(saved.lastCrop);
      plot.rotationBonus = saved.rotationBonus === true;
    }
  if (Array.isArray(data.resourceNodes))
    for (const node of world.resourceNodes) {
      const saved = record(
        data.resourceNodes.find((v) => record(v).id === node.id),
      );
      if (!Object.keys(saved).length) continue;
      node.active = saved.active !== false;
      node.health = number(
        saved.health,
        node.maxHealth,
        node.active ? 1 : 0,
        node.maxHealth,
      );
      node.respawnTimer = number(
        saved.respawnTimer,
        0,
        0,
        RESOURCES[node.kind].respawn,
      );
      if (!node.active && node.respawnTimer === 0) {
        node.active = true;
        node.health = node.maxHealth;
      }
    }
  const commands: CompanionCommand[] = [
    "follow",
    "gather",
    "guard",
    "retreat",
    "focus",
  ];
  if (Array.isArray(data.companions))
    for (const item of data.companions.slice(0, 8)) {
      const c = record(item),
        id = cropId(c.cropId);
      if (!id || number(c.health, 0) <= 0) continue;
      const crop = CROPS[id],
        hp = number(c.maxHealth, crop.companionHp, 1, 400);
      world.companions.push({
        id: world.nextId++,
        cropId: id,
        name:
          typeof c.name === "string"
            ? c.name.trim().slice(0, 10) || crop.companionName
            : crop.companionName,
        pos: point(c.pos, world.player.pos),
        velocity: { x: 0, y: 0 },
        radius: 22,
        health: number(c.health, hp, 1, hp),
        maxHealth: hp,
        speed: crop.companionSpeed * 1.35,
        damage: number(c.damage, crop.companionDamage, 1, 60),
        facing: 1.57,
        attackCooldown: 0,
        gatherCooldown: 0,
        producedTimer: number(c.producedTimer, 0, 0, 21),
        flash: 0,
        command: commands.includes(c.command as CompanionCommand)
          ? (c.command as CompanionCommand)
          : "follow",
        anchor: point(c.anchor, world.player.pos),
        focusEnemyId: null,
        reviveWard: c.reviveWard === true,
        possessed: false,
      });
    }
  if (Array.isArray(data.spoilingFood))
    for (const item of data.spoilingFood.slice(0, 32)) {
      const f = record(item),
        remaining = number(f.remaining, 0, 0, 70),
        amount = integer(f.amount, 0, 4);
      if (remaining && amount)
        world.spoilingFood.push({
          id: world.nextId++,
          remaining,
          amount,
          sourceName:
            typeof f.sourceName === "string"
              ? f.sourceName.slice(0, 10)
              : "灵植",
        });
    }
  const player = record(data.player);
  world.player.pos = point(player.pos, world.player.pos);
  world.player.health = number(player.health, 120, 1, 120);
  world.day = integer(data.day, 1, 9999) || 1;
  world.dayTime = number(data.dayTime, 0.28, 0, 0.9999);
  world.elapsed = number(data.elapsed, 0);
  world.attackMode = data.attackMode === "crescent" ? "crescent" : "seed";
  world.equipment = readEquipment(data.equipment);
  if (data.version === 1) {
    world.progress.awakened = world.companions.length;
    world.progress.capacity = world.companions.length > 4 ? 1 : 0;
    world.progress.watered = record(data.quest).watered ? 1 : 0;
  }
  world.progress.capacity = Math.max(
    world.progress.capacity,
    Math.ceil(Math.max(0, world.companions.length - 4) / 2),
  );
  world.selectedCompanionId = world.companions[0]?.id ?? null;
  return world;
}

export function readSave(storage: Pick<Storage, "getItem">, keys = [SAVE_KEY, PREVIOUS_KEY, LEGACY_KEY]): World | null {
  try {
    for (const key of keys) {
      const raw = storage.getItem(key);
      if (raw) {
        const world = deserialize(raw);
        if (world) return world;
      }
    }
    return null;
  } catch {
    return null;
  }
}
